import { InvestorStatus } from '@/features/investor-management/investor-management.def.ts';

/**
 * The reporting groups a geography scope selects, keyed by backend `Geography` CLR key, in report
 * order, labelled like the NAV by Investor Geography rows. The backend folds every stored region
 * onto one of them (`ToReportingGroup`), so a group also matches investors stored under a legacy region.
 */
export const SCOPE_GEOGRAPHY_LABELS = {
  Americas: 'Americas (AMER)',
  Emea: 'EMEA',
  AsiaPacific: 'Asia-Pacific',
} as const;

/** Reporting-group CLR key (`Emea`), the wire and URL form of a geography. Labels: `SCOPE_GEOGRAPHY_LABELS`. */
export type ScopeGeography = keyof typeof SCOPE_GEOGRAPHY_LABELS;

export const SCOPE_GEOGRAPHY_ORDER: readonly ScopeGeography[] = ['Americas', 'Emea', 'AsiaPacific'];

/** Enum CLR key (`Active`), the wire and URL form of an investor status. */
export type ScopeInvestorStatus = keyof typeof InvestorStatus;

/**
 * The one page scope shared by every metrics endpoint (NAV Breakdown, Investor Dashboard, Fund
 * Dashboard, Fund Detail). Every field is optional and an empty list means "no filter", so `{}` is
 * the unscoped page. Grid column filters are separate and never part of the scope.
 *
 * Valuation dates and ownership denominators are computed before the scope narrows the rows (S-07).
 */
export interface AnalyticsScope {
  /** Fund reference-data GUIDs. Repeated `fundIds` query param on GETs. Fund Detail ignores it. */
  fundIds?: string[];
  /** ISO `yyyy-MM`; each fund is valued on its latest NAV date on or before this month end. */
  navAsOfMonth?: string;
  status?: ScopeInvestorStatus;
  /** Reporting groups, any of. Repeated `geographies` query param on GETs. */
  geographies?: ScopeGeography[];
  /** Investor country display names (`'Hong Kong'`), the backend `Country` wire value. */
  countries?: string[];
  /** Legal-name country display names; NAV rows and transactions are matched per legal name. */
  legalNameCountries?: string[];
  /**
   * Share-class reference-data GUIDs. Narrows holdings to these classes everywhere, Fund Detail
   * included; a fund's valuation date stays fund-wide.
   */
  shareClassIds?: string[];
}

/** The scope fields as they sit flat at the top of a grid POST body; empty fields are omitted. */
export type AnalyticsScopeBody = AnalyticsScope;

/**
 * Scope changes a drill applies on top of the current page scope. A list replaces the current list
 * (drilling into one fund shows that fund, not the union); `undefined` or `[]` removes the dimension.
 */
export type AnalyticsScopePatch = Partial<AnalyticsScope>;

/** Where a drill goes: a route path plus the scope change to apply there (review L3.2-01). */
export interface DrillTarget {
  pathname: string;
  patch?: AnalyticsScopePatch;
}
